import { Link, useNavigate, useParams } from '@tanstack/react-router'
import type { SeasonCreate, WinnerCalcMethod } from '@topmeeps/shared'
import { useState } from 'react'
import { ApiError } from '../api/client'
import { useCreateSeason, useGroup, useGroupGames } from '../api/hooks'
import { ScreenShell } from '../components/ScreenShell'
import { BackButton, TopBar } from '../primitives'
import { winnerCalcDescription, winnerCalcLabel } from '../seasons/format'

const METHODS: ReadonlyArray<WinnerCalcMethod> = [
  'highest_current_rating',
  'most_rating_gained',
  'most_wins',
  'highest_win_pct',
  'most_plays',
]

const todayStr = (): string => {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const yearEndStr = (): string => {
  const d = new Date()
  return `${d.getFullYear()}-12-31`
}

const localDateToIso = (date: string, endOfDay: boolean): string => {
  const t = endOfDay ? '23:59:59' : '00:00:00'
  return new Date(`${date}T${t}`).toISOString()
}

export const SeasonCreateScreen = () => {
  const { id: groupId } = useParams({ from: '/group/$id/seasons/new' })
  const navigate = useNavigate()
  const group = useGroup(groupId)
  const games = useGroupGames(groupId)
  const create = useCreateSeason(groupId)

  const [name, setName] = useState('')
  const [startDate, setStartDate] = useState(todayStr())
  const [endDate, setEndDate] = useState(yearEndStr())
  const [includedGames, setIncludedGames] = useState<string[]>([])
  const [method, setMethod] = useState<WinnerCalcMethod>('most_wins')
  const [threshold, setThreshold] = useState<number>(5)
  const [error, setError] = useState<string | null>(null)

  const isAdmin = group.data?.role === 'admin'

  const toggleGame = (gameId: string) =>
    setIncludedGames((prev) =>
      prev.includes(gameId) ? prev.filter((g) => g !== gameId) : [...prev, gameId],
    )

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!name.trim()) {
      setError('Name is required')
      return
    }
    if (new Date(endDate) <= new Date(startDate)) {
      setError('End must be after start')
      return
    }

    const payload: SeasonCreate = {
      name: name.trim(),
      startAt: localDateToIso(startDate, false),
      endAt: localDateToIso(endDate, true),
      includedGames,
      winnerCalcMethod: method,
      minPlaysThreshold: method === 'highest_win_pct' ? threshold : null,
    }

    try {
      const row = await create.mutateAsync(payload)
      navigate({ to: '/season/$id', params: { id: row.id } })
    } catch (err) {
      if (err instanceof ApiError) setError(err.friendlyMessage)
      else setError(err instanceof Error ? err.message : 'unknown error')
    }
  }

  if (!isAdmin && group.data) {
    return (
      <ScreenShell>
        <TopBar
          left={
            <BackButton fallback={() => navigate({ to: '/group/$id', params: { id: groupId } })} />
          }
          title="New season"
        />
        <div style={{ padding: 16, color: 'var(--ink-2)' }}>
          Only group admins can create seasons.
        </div>
      </ScreenShell>
    )
  }

  return (
    <ScreenShell>
      <TopBar
        left={
          <BackButton fallback={() => navigate({ to: '/group/$id', params: { id: groupId } })} />
        }
        title="New season"
        sub={group.data?.name ?? null}
      />

      <form
        onSubmit={onSubmit}
        style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 18 }}
      >
        <Field label="Name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Autumn 26"
            maxLength={80}
            style={inputStyle}
            autoFocus
          />
        </Field>

        <div style={{ display: 'flex', gap: 12 }}>
          <Field label="Start" style={{ flex: 1 }}>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={inputStyle}
            />
          </Field>
          <Field label="End" style={{ flex: 1 }}>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={inputStyle}
            />
          </Field>
        </div>

        <div>
          <Label>Winner calculation</Label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
            {METHODS.map((m) => (
              <label
                key={m}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: 10,
                  border: `1px solid ${method === m ? 'var(--terra)' : 'var(--line-soft)'}`,
                  background: method === m ? 'var(--terra-bg)' : 'var(--paper)',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="method"
                  checked={method === m}
                  onChange={() => setMethod(m)}
                  style={{ marginTop: 3 }}
                />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{winnerCalcLabel(m)}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                    {winnerCalcDescription(m)}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {method === 'highest_win_pct' && (
          <Field label="Minimum plays to qualify">
            <input
              type="number"
              min={1}
              value={threshold}
              onChange={(e) => setThreshold(Math.max(1, Number(e.target.value) || 1))}
              style={{ ...inputStyle, width: 80 }}
            />
          </Field>
        )}

        <div>
          <Label>Included games</Label>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
            Leave empty to include every game in the group.
          </div>
          <div
            style={{
              marginTop: 8,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: 6,
            }}
          >
            {games.data?.map((g) => (
              <label
                key={g.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 10px',
                  border: '1px solid var(--line-soft)',
                  background: includedGames.includes(g.id) ? 'var(--gilt-bg)' : 'var(--paper)',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                <input
                  type="checkbox"
                  checked={includedGames.includes(g.id)}
                  onChange={() => toggleGame(g.id)}
                />
                {g.name}
              </label>
            ))}
          </div>
        </div>

        {error && (
          <div
            style={{
              padding: 10,
              background: 'var(--terra-bg)',
              color: 'var(--terra-ink)',
              border: '1px solid var(--terra)',
              borderRadius: 4,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <Link
            to="/group/$id"
            params={{ id: groupId }}
            className="btn-ghost"
            style={{ flex: 1, textAlign: 'center', textDecoration: 'none' }}
          >
            Cancel
          </Link>
          <button
            type="submit"
            className="btn-stamp"
            disabled={create.isPending}
            style={{ flex: 2 }}
          >
            {create.isPending ? 'Creating…' : 'Create season'}
          </button>
        </div>
      </form>
    </ScreenShell>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--paper)',
  border: '1px solid var(--line-soft)',
  borderRadius: 4,
  fontFamily: 'var(--font-display)',
  fontSize: 14,
  color: 'var(--ink)',
}

const Label = ({ children }: { children: React.ReactNode }) => (
  <div className="kicker" style={{ fontSize: 11 }}>
    {children}
  </div>
)

const Field = ({
  label,
  children,
  style,
}: {
  label: string
  children: React.ReactNode
  style?: React.CSSProperties
}) => (
  <div style={style}>
    <Label>{label}</Label>
    <div style={{ marginTop: 4 }}>{children}</div>
  </div>
)
